import { supabase } from '../../lib/supabase';
import type { StripeProduct, StripeSubscriptionInfo, LeadProduct } from '../../types/crm';
import type { CRMState, StripeSubscriptionMap, LeadProductsMap } from './types';
import type { Database } from '../../types/supabase';
import { readMrrBreakdown } from '../../utils/revenue';
import type { MRRResult, MrrReadResult } from '../../utils/revenue';

type Set = (partial: Partial<CRMState> | ((state: CRMState) => Partial<CRMState>)) => void;
type Get = () => CRMState;

type StripeProductRow = Database['public']['Tables']['stripe_products']['Row'];
type LeadProductRow = Database['public']['Tables']['lead_products']['Row'];
type StripeSubscriptionRow = Database['public']['Tables']['stripe_subscriptions']['Row'];

/**
 * Normalize a `stripe_products` DB row into the domain `StripeProduct`.
 * The DB stores `interval`/`product_type` as free-form strings and `metadata`
 * as nullable `Json`; the domain type narrows them to their canonical unions.
 */
function toStripeProduct(row: StripeProductRow): StripeProduct {
  return {
    id: row.id,
    stripe_product_id: row.stripe_product_id,
    stripe_price_id: row.stripe_price_id,
    name: row.name,
    description: row.description,
    unit_amount: row.unit_amount,
    currency: row.currency,
    interval: row.interval as StripeProduct['interval'],
    interval_count: row.interval_count,
    is_active: row.is_active,
    product_type: row.product_type as StripeProduct['product_type'],
    metadata: (row.metadata as Record<string, unknown> | null) ?? {},
    livemode: row.livemode,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

type StripeSubscriptionSelected = Pick<
  StripeSubscriptionRow,
  | 'customer_id'
  | 'subscription_id'
  | 'status'
  | 'price_id'
  | 'current_period_end'
  | 'cancel_at_period_end'
  | 'discount_percent'
  | 'discount_amount'
>;

/**
 * Normalize a `stripe_subscriptions` DB row into `StripeSubscriptionInfo`.
 * The query is filtered to active/trialing rows, so the DB enum status is
 * narrowed to the domain union; nullable id/flags fall back to safe defaults.
 */
function toStripeSubscriptionInfo(row: StripeSubscriptionSelected): StripeSubscriptionInfo {
  return {
    subscription_id: row.subscription_id ?? '',
    customer_id: row.customer_id,
    status: row.status as StripeSubscriptionInfo['status'],
    price_id: row.price_id,
    current_period_end: row.current_period_end,
    cancel_at_period_end: row.cancel_at_period_end ?? false,
    discount_percent: row.discount_percent,
    discount_amount: row.discount_amount,
  };
}

/**
 * Normalize a `lead_products` DB row into the domain `LeadProduct`.
 * `custom_interval`/`status` are free-form strings in the DB and narrowed here.
 */
function toLeadProduct(row: LeadProductRow): LeadProduct {
  return {
    id: row.id,
    lead_id: row.lead_id,
    stripe_product_id: row.stripe_product_id,
    stripe_price_id: row.stripe_price_id,
    custom_product_name: row.custom_product_name,
    custom_unit_amount: row.custom_unit_amount,
    custom_interval: (row.custom_interval as LeadProduct['custom_interval']) ?? null,
    quantity: row.quantity ?? 1,
    status: (row.status as LeadProduct['status']) ?? 'active',
    notes: row.notes,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
  };
}

export const createStripeSlice = (set: Set, get: Get) => ({
  stripeProducts: [] as StripeProduct[],
  stripeSubscriptions: {} as StripeSubscriptionMap,
  leadProducts: {} as LeadProductsMap,
  totalMRR: 0,
  totalARR: 0,
  forecastMRR: 0,
  forecastARR: 0,
  mrrDetail: null as MRRResult | null,
  mrrError: null as string | null,

  fetchStripeProducts: async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_products')
        .select('*')
        .eq('is_active', true)
        .order('unit_amount', { ascending: true });

      if (error) throw error;
      set({ stripeProducts: (data || []).map(toStripeProduct) });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch Stripe products';
      set({ error: message });
    }
  },

  fetchStripeSubscriptions: async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_subscriptions')
        .select('customer_id, subscription_id, status, price_id, current_period_end, cancel_at_period_end, discount_percent, discount_amount')
        .in('status', ['active', 'trialing']);

      if (error) throw error;

      const subscriptionMap: StripeSubscriptionMap = {};
      (data || []).forEach((sub) => {
        subscriptionMap[sub.customer_id] = toStripeSubscriptionInfo(sub);
      });

      set({ stripeSubscriptions: subscriptionMap });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch subscriptions';
      set({ error: message });
    }
  },

  fetchLeadProducts: async () => {
    try {
      const { data, error } = await supabase
        .from('lead_products')
        .select('*')
        .eq('status', 'active');

      if (error) throw error;

      const leadProductsMap: LeadProductsMap = {};
      (data || []).forEach((row) => {
        const lp = toLeadProduct(row);
        if (!leadProductsMap[lp.lead_id]) {
          leadProductsMap[lp.lead_id] = [];
        }
        leadProductsMap[lp.lead_id].push(lp);
      });

      set({ leadProducts: leadProductsMap });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch lead products';
      set({ error: message });
    }
  },

  syncStripeProducts: async () => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        return { success: false, message: 'Non authentifie' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-sync-products`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      if (!result.success) {
        return { success: false, message: result.error || 'Erreur de synchronisation' };
      }

      await get().fetchStripeProducts();
      return { success: true, message: `${result.synced} produits synchronises` };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur de synchronisation';
      return { success: false, message };
    }
  },

  syncStripeCustomers: async (mode: 'all' | 'new_only' | 'subscriptions_only' = 'new_only') => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        return { success: false, message: 'Non authentifie' };
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-sync-customers`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionData.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ mode }),
        }
      );

      const result = await response.json();

      // Log detailed results for debugging
      console.log('=== Stripe Sync Results ===');
      console.log('Total customers:', result.total_customers);
      console.log('Total subscriptions:', result.total_subscriptions);
      console.log('Created:', result.created);
      console.log('Updated:', result.updated);
      console.log('Skipped:', result.skipped);

      if (result.results && result.results.length > 0) {
        console.log('Details:');
        result.results.forEach((r: { customer_id: string; action: string; name?: string; email?: string; error?: string }) => {
          if (r.action === 'error') {
            console.error(`  ❌ ${r.name} (${r.email}): ${r.error}`);
          } else {
            console.log(`  ✓ ${r.action}: ${r.name} (${r.email})`);
          }
        });
      }
      console.log('=========================');

      if (!result.success) {
        return { success: false, message: result.error || 'Erreur de synchronisation' };
      }

      // Check if there were any errors in the results
      const errors = result.results?.filter((r: { action: string }) => r.action === 'error') || [];

      await get().fetchOnboardingLeads();
      await get().fetchStripeSubscriptions();

      let message = `${result.created} clients importes, ${result.updated} mis a jour`;
      if (errors.length > 0) {
        message += ` (${errors.length} erreurs - voir console)`;
      }

      return {
        success: true,
        message,
        created: result.created,
        updated: result.updated,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Erreur de synchronisation';
      console.error('Stripe sync error:', error);
      return { success: false, message };
    }
  },

  calculateRevenue: async () => {
    // The MRR has one definition, in SQL (get_mrr_breakdown, the same figure mrr-snapshot and
    // Otto read). A failed or unreadable answer keeps the previous figures and raises the
    // store error: never a silent 0 €.
    const { data, error } = await supabase.rpc('get_mrr_breakdown');
    const read: MrrReadResult = error
      ? { ok: false, error: error.message }
      : readMrrBreakdown(data);
    if (!read.ok) {
      // The screens show this instead of a figure (MetricsHeader, HomePage, RevenueDashboardPage).
      set({ mrrError: `Lecture du MRR impossible : ${read.error}` });
      return;
    }
    const mrrResult = read.value;
    const { opportunities } = get();

    const totalMRR = mrrResult.total;
    const totalARR = mrrResult.arr;

    // ── Pipeline + conversion (from opportunities) ──
    let pipelineMRR = 0;
    const wonCount = opportunities.filter(o => o.status === 'won').length;
    let lostCount = 0;

    opportunities.forEach((opp) => {
      if (opp.status === 'lost') lostCount += 1;
      if (opp.status === 'open' && opp.calculated_mrr && opp.stage?.order_index !== 1) {
        pipelineMRR += opp.calculated_mrr;
      }
    });

    const closedCount = wonCount + lostCount;
    const conversionRate = closedCount > 0 ? wonCount / closedCount : 0;
    const forecastMRR = totalMRR + (pipelineMRR * conversionRate);
    const forecastARR = forecastMRR * 12;

    set({ totalMRR, totalARR, forecastMRR, forecastARR, mrrDetail: mrrResult, mrrError: null });
  },
});
